<template>
  <div class="assignment-modal-overlay" @click="$emit('close')">
    <div class="assignment-modal" @click.stop>
      <div class="modal-header">
        <h3 v-if="!existingAssignment">Assigna dig till "{{ item?.name }}"</h3>
        <h3 v-else>Uppdatera din assignment</h3>
        <button @click="$emit('close')" class="close-btn">×</button>
      </div>
      
      <div class="modal-content">
        <div v-if="item" class="item-info">
          <h4>{{ item.name }}</h4>
          <p>{{ item.description }}</p>
          <div class="item-details">
            <span class="priority-badge" :class="priorityClass">
              Prioritet: {{ item.priority }}
            </span>
            <span v-if="item.price" class="price">
              {{ formatPrice(item.price) }} SEK
            </span>
          </div>
        </div>
        
        <form @submit.prevent="handleSubmit" class="assignment-form">
          <div class="form-group">
            <label for="comment">Kommentar:</label>
            <textarea
              id="comment"
              v-model="comment"
              required
              :disabled="loading"
              placeholder="Skriv en kommentar om varför du vill köpa detta eller annan relevant information..."
              rows="4"
            ></textarea>
          </div>
          
          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          
          <div class="form-actions">
            <button 
              type="submit" 
              :disabled="loading || !comment.trim()" 
              class="submit-btn"
            >
              <span v-if="loading">⏳</span>
              <span v-else-if="existingAssignment">Uppdatera assignment</span>
              <span v-else>Assigna dig</span>
            </button>
            
            <button 
              v-if="existingAssignment" 
              type="button" 
              @click="handleRemove" 
              :disabled="loading" 
              class="remove-btn"
            >
              <span v-if="removing">⏳</span>
              <span v-else>Ta bort assignment</span>
            </button>
            
            <button type="button" @click="$emit('close')" :disabled="loading" class="cancel-btn">
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'AssignmentModal',
  emits: ['close', 'assigned', 'updated', 'removed'],
  props: {
    item: {
      type: Object,
      default: null
    },
    existingAssignment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      comment: '',
      loading: false,
      removing: false,
      error: null
    };
  },
  computed: {
    priorityClass() {
      if (!this.item) return '';
      if (this.item.priority <= 3) return 'priority-high';
      if (this.item.priority <= 6) return 'priority-medium';
      return 'priority-low';
    }
  },
  mounted() {
    // Pre-fill comment if editing existing assignment
    if (this.existingAssignment) {
      this.comment = this.existingAssignment.comment || '';
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('sv-SE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(price);
    },
    
    async handleSubmit() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = authService.getToken();
        if (!token) {
          throw new Error('Du måste vara inloggad');
        }
        
        if (this.existingAssignment) {
          // Update existing assignment
          await apiService.updateAssignment(
            this.existingAssignment.id, 
            this.comment.trim(), 
            token
          );
          this.$emit('updated');
        } else {
          // Create new assignment
          await apiService.assignToItem(
            this.item.id, 
            this.comment.trim(), 
            token
          );
          this.$emit('assigned');
        }
        
      } catch (error) {
        console.error('Assignment operation failed:', error);
        
        if (error.message.includes('already assigned')) {
          this.error = 'Du är redan assignad till detta item. Uppdatera din befintliga assignment istället.';
        } else if (error.message.includes('unauthorized')) {
          this.error = 'Du måste vara inloggad för att göra detta.';
        } else {
          this.error = error.message || 'Ett fel uppstod';
        }
      } finally {
        this.loading = false;
      }
    },
    
    async handleRemove() {
      if (!confirm('Är du säker på att du vill ta bort din assignment?')) {
        return;
      }
      
      this.removing = true;
      this.error = null;
      
      try {
        const token = authService.getToken();
        if (!token) {
          throw new Error('Du måste vara inloggad');
        }
        
        await apiService.removeAssignment(this.existingAssignment.id, token);
        this.$emit('removed');
        
      } catch (error) {
        console.error('Remove assignment failed:', error);
        this.error = error.message || 'Kunde inte ta bort assignment';
      } finally {
        this.removing = false;
      }
    }
  }
};
</script>

<style scoped>
.assignment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.assignment-modal {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  background: #f8f9fa;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #e9ecef;
}

.modal-content {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.item-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-left: 4px solid #007BFF;
}

.item-info h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
}

.item-info p {
  margin: 0 0 1rem 0;
  color: #666;
  line-height: 1.5;
}

.item-details {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.priority-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
}

.priority-high {
  background-color: #dc3545;
}

.priority-medium {
  background-color: #ffc107;
  color: #333;
}

.priority-low {
  background-color: #28a745;
}

.price {
  font-weight: 600;
  color: #007BFF;
}

.assignment-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
}

.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;
}

.form-group textarea:focus {
  outline: none;
  border-color: #007BFF;
}

.form-group textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.error-message {
  background-color: #ffe6e6;
  color: #d63031;
  padding: 0.75rem;
  border-radius: 5px;
  text-align: center;
}

.form-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.submit-btn,
.remove-btn,
.cancel-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.submit-btn {
  background-color: #28a745;
  color: white;
  flex: 1;
}

.submit-btn:hover:not(:disabled) {
  background-color: #218838;
}

.submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.remove-btn {
  background-color: #dc3545;
  color: white;
}

.remove-btn:hover:not(:disabled) {
  background-color: #c82333;
}

.remove-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.cancel-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

@media (max-width: 576px) {
  .assignment-modal {
    width: 95%;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h3 {
    font-size: 1.1rem;
  }
  
  .modal-content {
    padding: 1rem;
  }
  
  .item-info {
    padding: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .submit-btn {
    flex: none;
  }
}
</style>
